import { createSlice, type PayloadAction, createAction } from '@reduxjs/toolkit'
import { type IDeviceStatusKPIData } from '../services/getDeviceStatusSummaryService'

export interface IDeviceKPIStatusState {
  isLoading: boolean
  error: string | undefined
  data: any | null
  httpStatus: number | undefined
}

export const initialState: IDeviceKPIStatusState = {
  isLoading: false,
  error: undefined,
  data: null,
  httpStatus: 0
}

const deviceKPIStatusSummarySlice = createSlice({
  name: 'DeviceKPIStatusSummayState',
  initialState,
  reducers: {
    deviceKPIStatusSummayLoadingAction: (state) => {
      return {
        isLoading: true,
        error: undefined,
        data: null,
        httpStatus: 0
      }
    },
    deviceKPIStatusSummayUpdateAction: (state, action: PayloadAction<{ data: IDeviceStatusKPIData, httpStatus: number }>) => {
      return {
        isLoading: false,
        error: undefined,
        data: action.payload.data,
        httpStatus: action.payload.httpStatus
      }
    },
    deviceKPIStatusSummayErrorAction: (state, action: PayloadAction<{ errorMessage: string, httpStatus: number | undefined }>) => {
      return {
        isLoading: false,
        error: action.payload.errorMessage,
        data: null,
        httpStatus: action.payload.httpStatus
      }
    },
    updateKPIDataAction: (state, action: PayloadAction<{ deviceId: string, kpi_data: any[] }>) => {
      // Only update if we have existing data
      if (Boolean(state.data?.kpi_data) && Array.isArray(state.data.kpi_data)) {
        const updatedKPIData = [...state.data.kpi_data]

        // Update existing KPI data with new values from WebSocket
        action.payload.kpi_data.forEach((newKPI) => {
          const existingIndex = updatedKPIData.findIndex(
            (existing) => existing.kpi_definition_id === newKPI.kpi_definition_id
          )

          if (existingIndex !== -1) {
            // Update the existing KPI with new values, preserving the structure
            updatedKPIData[existingIndex] = {
              ...updatedKPIData[existingIndex],
              kpi_value: newKPI.kpi_value,
              formattedValue: newKPI.formattedValue,
              colorValue: newKPI.colorValue,
              timestamp: newKPI.timestamp,
              execution_status: newKPI.execution_status,
              units: newKPI.units,
              kpi_name: newKPI.kpi_name
            }
          } else {
            // If the KPI doesn't exist in current data, add it
            updatedKPIData.push({
              kpi_value: newKPI.kpi_value,
              formattedValue: newKPI.formattedValue,
              colorValue: newKPI.colorValue,
              timestamp: newKPI.timestamp ?? new Date().toISOString(),
              execution_status: newKPI.execution_status ?? null,
              units: newKPI.units,
              kpi_name: newKPI.kpi_name,
              kpi_definition_id: newKPI.kpi_definition_id,
              is_custom_kpi: newKPI.is_custom_kpi ?? false
            })
          }
        })

        return {
          ...state,
          data: {
            ...state.data,
            kpi_data: updatedKPIData
          }
        }
      } else {
        // If no existing data, create initial state with the new KPI data
        return {
          ...state,
          data: {
            kpi_data: action.payload.kpi_data.map(kpi => ({
              ...kpi,
              timestamp: kpi.timestamp ?? new Date().toISOString(),
              execution_status: kpi.execution_status ?? null
            }))
          }
        }
      }
    }
  }
})

// Create action for KPI data updates
export const updateKPIData = createAction<{ deviceId: string, kpi_data: any[] }>('deviceStatus/updateKPIData')

export const {
  deviceKPIStatusSummayLoadingAction,
  deviceKPIStatusSummayUpdateAction,
  deviceKPIStatusSummayErrorAction,
  updateKPIDataAction
} = deviceKPIStatusSummarySlice.actions

export default deviceKPIStatusSummarySlice.reducer
